import { Checkbox, Field, IMessageProps, Link, Spinner, TextOutput } from 'Components/Core';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { GridFieldSection } from 'Components/Layout';
import Grid from 'Components/Layout/Grid';
import { IWorksheetComponent } from 'Components/Worksheets/Core';
import { actions } from 'Data/ReduxViewModels/AssignedValuesTests/slice';
import { hasNonEmptyValue, valueOrDefault } from 'Lib/Any';
import { FormatTypes } from 'Lib/Format';
import { pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { orderBy } from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as amddActions } from '../../../../../Data/ReduxViewModels/AssignedValuesTests';
import { AssignedValueTest, MaximumDryDensityTest, maxTestsToSelect } from '../../types';

let AsAvailableTestsGrid: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dispatch = useDispatch();
  const querying = useSelector((state) => state.assignedMaximumDryDensityTests.status.querying);
  let colHeaders = [
    'Sample ID',
    'Material',
    'Maximum Dry Density (t/m³)',
    'Optimum Moisture Content (%)',
    'Wet Oversize (%)',
    'Oversize Sieve Aperture (mm)',
    'Date Sampled',
    'Select',
  ];

  /**
   * Retrieve available MDD tests from state.
   */
  const data: MaximumDryDensityTest[] = useSelector(
    pipe((state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets, valueOrDefault([])),
    deepEquals
  );

  /**
   * Variable to indicate if the maximum amount of tests have been selected (6)
   */
  const maxTestsAreSelected: boolean = useSelector(
    pipe(
      (state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets,
      valueOrDefault([]),
      (mddTests: MaximumDryDensityTest[]) => mddTests?.filter((row) => row.selected)?.length === maxTestsToSelect
    )
  );

  const allSelectedTestsHaveSameOversizeSieveAperture = useSelector(
    pipe(
      (state) => state.assignedMaximumDryDensityTests.mddTests?.Worksheets ?? [],
      (mddTests: MaximumDryDensityTest[]) => mddTests?.filter((row) => row.selected),
      (selectedTests: MaximumDryDensityTest[]) =>
        !maxTestsAreSelected ||
        selectedTests?.every((row) => row.oversizeSieveApertureSizeId === selectedTests[0].oversizeSieveApertureSizeId)
    )
  ) as boolean;

  const FindIndexByTestId = (id: any) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].id === id) {
        return i;
      }
    }
    return 0;
  };

  /**
   * Handle "Selected" checkbox click.
   */
  const handleCheckboxChange = (row: any, testId: any) => {
    let currentRowSelectedValue = row.selected ?? false;
    dispatch(
      actions.setTestSelected({
        value: !currentRowSelectedValue,
        index: FindIndexByTestId(testId),
      })
    );
  };
  const AssignedValueTestsCount = useSelector(
    pipe(createWorksheetSelector!('AssignedValueTests'), (avTests: AssignedValueTest[]) => avTests?.length)
  );
  /**
   * Use helper class to retrieve complex messages or state messages.
   */
  const gridMessages = useSelector(
    pipe(
      createWorksheetSelector!('ValueAssignment.LatestSourceEntitySampledDate'),
      (latestSourceEntitySampledDate: Date) => {
        const LatestSelectedMddDateSampled = data?.filter((row) => row.selected)[0]?.dateSampled as Date;
        const SelectedTests = data?.filter((row) => row.selected)?.length;

        let gridMessages: IMessageProps[] = [];

        if (data?.length > 0) {
          if ((SelectedTests ?? 0) > 0 && SelectedTests !== maxTestsToSelect) {
            gridMessages.push({
              severity: 'info',
              message: 'Exactly six tests must be selected to assign values.',
            });
          }
        }
        //Checking the Assigned Value Tests prevents a niche situation where this message can get stuck after deleting a test.
        //It shouldnt occur if there are no Assigned Value Tests anyway.
        if (AssignedValueTestsCount !== 0 && LatestSelectedMddDateSampled < latestSourceEntitySampledDate) {
          gridMessages.push({
            severity: 'error',
            message:
              'Newest selected test has been sampled prior to newest test from previous assigned value, newer tests should be used.',
          });
        }
        if (!allSelectedTestsHaveSameOversizeSieveAperture)
          gridMessages.push({
            severity: 'error',
            message: 'Each test should have the same oversize sieve aperture size.',
          });
        return gridMessages;
      }
    )
  );

  /**
   * Fire off the api request to calculate results from those tests.
   * PostSelectedTestResults will handle the data manipulation and fire off only when 6 tests are selected
   */
  useEffect(() => {
    if (!maxTestsAreSelected) {
      //Cleanup will delete the interim results and messages
      dispatch(amddActions.postSelectedTestsResultsCleanup());
    } else {
      dispatch(amddActions.postSelectedTestsResults());
    }
  }, [maxTestsAreSelected, dispatch]);

  const getFieldSeverity = (RowIsInvalid: boolean) => {
    return RowIsInvalid ? 'error' : undefined;
  };

  const gridData = orderBy(data, ['dateSampled', 'sampleId'], ['desc', 'desc'])?.map((row) => [
    <Link openInNewTab to={`/worksheets/${row.id}`}>
      {row.sampleId}
    </Link>,
    <Field input={<TextOutput />} value={row.material} severity={getFieldSeverity(row.testIsInvalid)} />,
    <Field
      input={<TextOutput />}
      value={row.maximumDryDensity?.value}
      format={{ uom: 't/m³', nearest: 0.01 }}
      severity={getFieldSeverity(row.testIsInvalid)}
    />,
    <Field
      input={<TextOutput />}
      value={row.optimumMoistureContent}
      format={{ nearest: 0.5 }}
      severity={getFieldSeverity(row.testIsInvalid)}
    />,
    <Field
      input={<TextOutput />}
      value={row.oversizePercent ?? 0}
      format={{ nearest: 1 }}
      severity={getFieldSeverity(row.testIsInvalid)}
    />,
    <Field
      input={<TextOutput />}
      value={row.oversizeSieveApertureSize ?? 0}
      format={{ nearest: 0.1 }}
      severity={getFieldSeverity(row.testIsInvalid)}
    />,
    <Field
      input={<TextOutput />}
      value={row.dateSampled}
      severity={getFieldSeverity(row.testIsInvalid)}
      format={{ type: FormatTypes.DateTime }}
    />,
    <Field
      input={<Checkbox />}
      severity={getFieldSeverity(row.testIsInvalid)}
      disabled={row.testIsInvalid || (!row.selected && maxTestsAreSelected)} //Disable checkbox if 6 tests have been selected
      value={row.selected ?? false}
      onChange={() => handleCheckboxChange(row, row.id)}
    />,
  ]);

  return (
    <>
      {!querying || !hasNonEmptyValue(gridData) ? (
        <GridFieldSection heading="Available Tests" messages={gridMessages} visible={hasNonEmptyValue(gridData)}>
          <Grid colHeaders={colHeaders} data={gridData} />
        </GridFieldSection>
      ) : (
        <div style={{ position: 'relative', height: '50vh' }}>
          <Spinner large />
        </div>
      )}
    </>
  );
};

AsAvailableTestsGrid = withInjectedProps(AsAvailableTestsGrid);

export { AsAvailableTestsGrid };
export default AsAvailableTestsGrid;
